<script>
  import { createEventDispatcher } from 'svelte';
  import Input from '@components/Input.svelte';
  import Button from '@components/Button.svelte';
  import SimpleAvatar from './SimpleAvatar.svelte';
  import Auth from '@utils/auth';
  import Notification from '@utils/notification';
  import UserAjax from '@ajax/user';
  import { getCurrentUserStore } from '@stores/current_user';
  import Track from '@utils/track';

  const dispatch = createEventDispatcher();

  export let mobile = false;
  export let inviteUser;
  export let showInvitedUserBanner = true;
  export let prefilledUsername = null;

  let usernameEl;
  let usernameValue = prefilledUsername;
  let loadingUsername = false;
  let usernameInvalidMessage;
  let currentUser = getCurrentUserStore('default');

  $: enabledUsernameNextButton = usernameValue && usernameValue !== '' && !usernameInvalidMessage;

  let updateCurrentUserUsername = async function () {
    try {
      let updatedUser = await UserAjax.update(currentUser, { username: usernameEl.value });
      Track.usernameEntered();
      dispatch('finished', { user: updatedUser });
    } catch (err) {
      if (err && err.full_errors && err.full_errors.length > 0) {
        Notification.send({
          text: err.full_errors[0],
        });
      }
    }
  };

  // we should load these up at this step so they are ready by the time congrats step is reached.
  let loadCongratsImages = function () {
    new Image().src = '/images/app-invites/party.svg';
    new Image().src = '/images/app-invites/clapping-hands.svg';
    new Image().src = '/images/app-invites/smiling-face.png';
    new Image().src = '/images/app-invites/smiling-face@2x.png';
  };

  let validateUsername = function () {
    let username = usernameEl.value;

    if (!Auth.validUsername(username)) {
      usernameInvalidMessage =
        'Username can only contain letters, numbers or underscore. (must start with a letter or number)';
    } else {
      usernameInvalidMessage = undefined;
    }
  };

  let checkUsername = async function () {
    if (!enabledUsernameNextButton) {
      return;
    }

    validateUsername();

    if (usernameInvalidMessage) {
      return;
    }

    loadingUsername = true;

    try {
      let resp = await UserAjax.checkUsername(usernameEl.value);

      if (resp) {
        usernameInvalidMessage = 'Username is taken. Please select another one';
      } else {
        await updateCurrentUserUsername();
      }
    } catch {}

    loadingUsername = false;
  };
</script>

<style style="text/scss" lang="scss">
  @import '../../../vendor/design-system.scss';
  .reserve-username {
    display: flex;
    padding: 20px;
    min-height: calc(100dvh - 114px);

    .content-container {
      padding-bottom: 44px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-direction: column;
      max-width: 440px;
      margin: 0 auto;
      gap: 24px;
      flex: 1;
    }

    .ui-title {
      font-size: 42px;
      line-height: 38px;
      text-align: center;
      font-weight: 800;
      text-transform: uppercase;
      margin-top: 64px;
      margin-bottom: 16px;
      color: #717171;
    }

    .ui-subtitle {
      font-size: 16px;
      line-height: 24px;
      text-align: center;
      color: #7f7f7f;
      margin-bottom: 32px;
    }

    .input-container {
      margin-left: auto;
      margin-right: auto;
      text-align: left;
      position: relative;

      .at_symbol {
        position: absolute;
        z-index: 1;
        top: 14px;
        left: 14px;
      }
    }
  }

  .button-container {
    width: 100%;
  }

  .mobile {
    padding: 10px 20px 30px;

    .ui-title {
      margin-top: 32px;
    }

    &.reserve-username {
      min-height: calc(100dvh - 60px);
    }

    .input-container {
      width: 100%;
      margin-top: 28px;
    }

    .invited-user-header {
      .top-row {
        display: block;
      }
    }
  }

  .invited-user-header {
    margin: 0 auto;
    color: $neutral-gray;

    .top-row {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2px;
      padding: 8px 0;
    }

    .avatar-wrapper {
      margin-bottom: 8px;
    }

    .name {
      color: $modal-black;
      font-weight: $font-weight-bold;
    }

    .bonus {
      padding: 4px 12px;
      background: #e7f9f3;
      border-radius: 16px;
      font-weight: $font-weight-semi-bold;
      color: #2caf87;
      white-space: nowrap;
    }

    .bold {
      font-weight: 600;
    }
  }
  .top-row-container {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  .bonus-banner-container {
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    /* or 143% */
    text-align: center;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;

    color: $neutral-gray;
    padding: 20px;

    border-radius: 12px;
    border: 1px solid rgba(77, 175, 255, 0.4);
    background: linear-gradient(
        90deg,
        rgba(77, 175, 255, 0.04) -0.14%,
        rgba(108, 159, 255, 0.04) 14.9%,
        rgba(123, 153, 255, 0.04) 23.92%,
        rgba(204, 161, 247, 0.04) 46.99%,
        rgba(234, 169, 178, 0.04) 64.04%,
        rgba(249, 173, 143, 0.04) 81.09%,
        rgba(252, 183, 71, 0.04) 97.13%
      ),
      #fff;

    .ui-bonus-percentage {
      font-weight: $font-weight-bold;
      color: #18906c;
    }
    .ui-bonus-days {
      color: $modal-black;
      font-weight: $font-weight-bold;
    }
  }
</style>

<div class="reserve-username" class:mobile={mobile} use:loadCongratsImages>
  <div class="content-container">
    <div>
      {#if inviteUser && showInvitedUserBanner}
        <div class="invited-user-header">
          <div class="top-row-container">
            <div class="top-row">
              <div class="avatar-wrapper">
                <SimpleAvatar width={32} src={inviteUser.avatar_url} user={inviteUser} />
              </div>
              <div class="name">{inviteUser.name}</div>
              <p class="text">invited you to join Benable!</p>
            </div>
            <div class="bonus-banner-container {mobile ? 'mobile' : ''}">
              <div class="ui-bonus-header">
                Thanks to {inviteUser.name}, you're earning a <span class="ui-bonus-percentage">10% bonus</span> on your
                commissions for the <span class="ui-bonus-days">next 30 days!</span>
              </div>
            </div>
          </div>
        </div>
      {/if}
      <div>
        <div class="ui-title national-2-narrow">Reserve your Benable username</div>
        <div class="ui-subtitle">You can always change it later</div>

        <div class="input-container">
          <div class="at_symbol">@</div>

          <form autocomplete="off" method="post" action="#" on:submit|preventDefault>
            <Input
              bind:value={usernameValue}
              bind:inputEl={usernameEl}
              on:keydown={(e) => {
                if (e.detail.which === 32) {
                  e.detail.event.preventDefault();
                }
                // Prevent ESC key from closing the modal
                if (e.detail.which === 27) {
                  e.detail.event.preventDefault();
                  e.detail.event.stopPropagation();
                }
              }}
              on:keyup={(e) => {
                if (e.detail.which === 13) {
                  checkUsername();
                } else {
                  validateUsername();
                }
              }}
              placeholder="username"
              focus={true}
              loading={loadingUsername}
              error={usernameInvalidMessage}
              inputStyle="padding-left: 29px;"
              inputProps={{
                autocomplete: 'off',
                autocorrect: 'off',
                autocapitalize: 'off',
                spellcheck: 'false',
              }} />
          </form>
        </div>
      </div>
    </div>

    <div class="button-container">
      {#if loadingUsername}
        <Button full disabled={true}><i class="fa fa-spinner fa-spin" /></Button>
      {:else}
        <Button
          class="next-btn test-username-next-btn"
          full
          on:click={checkUsername}
          disabled={!enabledUsernameNextButton}>
          Next
        </Button>
      {/if}
    </div>
  </div>
</div>
